import React from "react";
import PropTypes from "prop-types";

import { Alert, Button, Card, Descriptions, Result, Space, Spin, Tag, Typography } from "antd";

import {
    ACTIVE_PAYMENT_STATUSES,
    DELIVERY_STATUS_LABELS,
    DELIVERY_TYPE_LABELS,
    ORDER_STATUS_LABELS,
    PAYMENT_STATUS_LABELS,
    RETRYABLE_PAYMENT_STATUSES,
    formatAmount,
    formatDate
} from "../../../../../../constants/marketplaceCheckout";

const { Text } = Typography;

/** Suivi de la livraison (ou du retrait) d'une commande. */
export const DeliveryTracking = ({ delivery }) => {
    if (!delivery) {
        return null;
    }
    const status = DELIVERY_STATUS_LABELS[delivery.status] || { label: delivery.status };
    const pickup = delivery.deliveryType === "DELIVERY_BY_BUYER";
    return (
        <Descriptions size="small" column={1} bordered title="Suivi de la livraison">
            <Descriptions.Item label="Mode">{DELIVERY_TYPE_LABELS[delivery.deliveryType]?.title}</Descriptions.Item>
            <Descriptions.Item label="Statut"><Tag color={status.color}>{status.label}</Tag></Descriptions.Item>
            {pickup ? (
                <Descriptions.Item label="Bordereau de retrait">{delivery.pickupReference}</Descriptions.Item>
            ) : (
                <Descriptions.Item label="N° de suivi">{delivery.trackingNumber}</Descriptions.Item>
            )}
            {!pickup && (
                <Descriptions.Item label="Transporteur">
                    {delivery.providerName}
                    {delivery.providerMode === "MOCK" && <Tag color="orange" style={{ marginLeft: 8 }}>Simulé</Tag>}
                </Descriptions.Item>
            )}
            {pickup && delivery.pickupPointName && (
                <Descriptions.Item label="Point de retrait">
                    {delivery.pickupPointName}{delivery.pickupPointAddress ? ` — ${delivery.pickupPointAddress}` : ""}
                </Descriptions.Item>
            )}
            {!pickup && (
                <Descriptions.Item label="Adresse">
                    {[delivery.deliveryAddressLine, delivery.deliveryDistrict, delivery.deliveryCity].filter(Boolean).join(", ")}
                    {delivery.deliveryLandmark ? ` (${delivery.deliveryLandmark})` : ""}
                </Descriptions.Item>
            )}
            {delivery.estimatedDeliveryDate && (
                <Descriptions.Item label="Livraison estimée">{formatDate(delivery.estimatedDeliveryDate)}</Descriptions.Item>
            )}
        </Descriptions>
    );
};

DeliveryTracking.propTypes = { delivery: PropTypes.object };

/** Commande créée après confirmation du paiement. */
export const OrderConfirmation = ({ order, delivery, onClose }) => {
    const status = ORDER_STATUS_LABELS[order.status] || { label: order.status, color: "green" };
    return (
        <Space orientation="vertical" size="large" style={{ width: "100%" }}>
            <Result
                status="success"
                title={`Paiement confirmé — commande n° ${order.orderNumber ?? "—"}`}
                subTitle={`${formatAmount(order.totalAmount, order.currency)} (produit ${formatAmount(order.productAmount, order.currency)} + livraison ${formatAmount(order.deliveryCost, order.currency)})`}
                extra={<Tag color={status.color}>{status.label}</Tag>}
            />
            {delivery ? (
                <DeliveryTracking delivery={delivery} />
            ) : (
                <Alert type="info" showIcon title="Préparation de la livraison en cours..." />
            )}
            {onClose && <Button type="primary" block onClick={onClose}>Fermer</Button>}
        </Space>
    );
};

OrderConfirmation.propTypes = {
    order: PropTypes.object.isRequired,
    delivery: PropTypes.object,
    onClose: PropTypes.func
};

/**
 * État d'un paiement déjà initié : en attente (prompt mobile, redirection,
 * agence), confirmé (commande) ou terminé sans encaissement (nouvel essai).
 */
const PaymentStatusPanel = ({ payment, order, delivery, onRetry, onClose }) => {
    if (order) {
        return <OrderConfirmation order={order} delivery={delivery} onClose={onClose} />;
    }
    if (payment.status === "SUCCEEDED") {
        return (
            <Result
                status="success"
                title="Paiement confirmé"
                subTitle="Création de votre commande en cours..."
                extra={<Spin />}
            />
        );
    }
    if (RETRYABLE_PAYMENT_STATUSES.includes(payment.status)) {
        return (
            <Result
                status="warning"
                title={PAYMENT_STATUS_LABELS[payment.status]?.label || payment.status}
                subTitle="Aucun montant n'a été débité pour cette tentative. Vous pouvez réessayer sans renégocier l'accord."
                extra={onRetry && <Button type="primary" onClick={onRetry}>Réessayer le paiement</Button>}
            />
        );
    }
    const statusView = PAYMENT_STATUS_LABELS[payment.status];
    const action = payment.nextAction;
    return (
        <Card>
            <Space orientation="vertical" style={{ width: "100%" }}>
                <Tag color={statusView?.color}>{statusView?.label || payment.status}</Tag>
                <Text>Montant : <strong>{formatAmount(payment.amount, payment.currency)}</strong></Text>
                {action?.type === "MOBILE_PROMPT" && (
                    <Alert type="info" showIcon title="Validez sur votre téléphone" description={action.message} />
                )}
                {action?.type === "CASH_AT_AGENCY" && (
                    <Alert type="info" showIcon title="Paiement au siège ASSIGANTO" description={action.message} />
                )}
                {action?.type === "REDIRECT" && action.url && (
                    <Button type="primary" onClick={() => window.location.assign(action.url)}>
                        Continuer vers la page de paiement sécurisée
                    </Button>
                )}
                {ACTIVE_PAYMENT_STATUSES.includes(payment.status) && action?.type !== "CASH_AT_AGENCY" && (
                    <Text type="secondary"><Spin size="small" /> En attente de la confirmation du prestataire...</Text>
                )}
                <Text type="secondary">Référence de paiement (bordereau) : {payment.reference}</Text>
            </Space>
        </Card>
    );
};

PaymentStatusPanel.propTypes = {
    payment: PropTypes.object.isRequired,
    order: PropTypes.object,
    delivery: PropTypes.object,
    onRetry: PropTypes.func,
    onClose: PropTypes.func
};

export default PaymentStatusPanel;
